import { getAuth } from "firebase/auth";
import { showQuotaDialogOnDenial } from "./prediction-quota-dialog";
import {
  confirmRenderAdmissionRequest,
  getRenderAdmissionRequest,
  renderAdmissionFingerprint,
} from "./render-admission-request";

export async function preparePythonPrediction<
  const T extends { predictionId?: string; publicTeamId?: string | null },
>(
  endpoint: string,
  input: T,
  getCurrentTeamId: () => string | null | undefined,
  // Python jobs finish before their status is written; other admissions prove it with requestComplete.
  { statusProvesCompletion = true }: { statusProvesCompletion?: boolean } = {},
) {
  const userId = getAuth().currentUser?.uid;
  const publicTeamId = getCurrentTeamId();
  if (!userId) {
    throw new Error("Sign in before generating images.");
  }
  if (input.publicTeamId != null && input.publicTeamId !== publicTeamId) {
    throw new Error("Account or team changed. Retry in the selected team.");
  }
  const { predictionId: suppliedId, ...parameters } = input;
  // Freeze the same JSON sent to the callable; retain only its scoped digest in retry storage.
  const bodyJson = JSON.stringify(
    { ...parameters, publicTeamId: publicTeamId ?? undefined },
    (_key, value) =>
      value && typeof value === "object" && !Array.isArray(value)
        ? Object.fromEntries(Object.entries(value).sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0)))
        : value,
  );
  const fingerprint = await renderAdmissionFingerprint({
    endpoint,
    userId,
    publicTeamId: publicTeamId ?? "personal",
    numOutputs: 1,
    body: bodyJson,
  });
  const predictionId = await getRenderAdmissionRequest(fingerprint, suppliedId);
  if (typeof predictionId !== "string" || !/^[a-zA-Z0-9_-]{1,128}$/.test(predictionId)) {
    throw new Error("Invalid prediction request key.");
  }
  const assertCurrentScope = () => {
    if (getAuth().currentUser?.uid !== userId || getCurrentTeamId() !== publicTeamId) {
      throw new Error("Account or team changed. Retry in the selected team.");
    }
  };
  assertCurrentScope();
  return {
    request: { ...JSON.parse(bodyJson), predictionId } as T & { predictionId: string },
    assertCurrentScope,
    async confirm(data: unknown) {
      assertCurrentScope();
      if (!data || typeof data !== "object" || !("ok" in data) || typeof data.ok !== "boolean") {
        throw new Error("Invalid prediction acknowledgement. Retry the same request.");
      }
      const releaseRequest = async () => {
        try {
          await confirmRenderAdmissionRequest(fingerprint, predictionId);
        } catch {
          console.error("python_prediction_receipt_cleanup_failed");
        }
        assertCurrentScope();
      };
      if (!data.ok) {
        showQuotaDialogOnDenial(data);
        // A 503 leaves admission uncertain, so only that refusal keeps the key for a same-key retry.
        if (statusProvesCompletion && !("code" in data && data.code === 503)) {
          await releaseRequest();
        }
        return;
      }
      if (!("predictionId" in data) || data.predictionId !== predictionId) {
        throw new Error("Invalid prediction acknowledgement. Retry the same request.");
      }
      // Accepted/processing replies and lost responses retain the key across tabs and reloads.
      const status = "status" in data ? data.status : undefined;
      const output = "output" in data ? data.output : undefined;
      const requestComplete = "requestComplete" in data ? data.requestComplete : undefined;
      if (
        (status !== undefined &&
          (typeof status !== "string" ||
            !["starting", "processing", "succeeded", "failed", "canceled"].includes(status))) ||
        (output !== undefined &&
          (!Array.isArray(output) ||
            output.length > 10 ||
            !output.every((value) => typeof value === "string" && value.length > 0))) ||
        (requestComplete !== undefined &&
          (requestComplete !== true ||
            typeof status !== "string" ||
            !["succeeded", "failed", "canceled"].includes(status)))
      ) {
        throw new Error("Invalid prediction acknowledgement. Retry the same request.");
      }
      const stopped = statusProvesCompletion && (status === "failed" || status === "canceled");
      if (
        status !== "succeeded" &&
        requestComplete !== true &&
        !stopped &&
        !(
          status === undefined &&
          Array.isArray(output) &&
          output.length > 0 &&
          output.every((value) => typeof value === "string" && value.length > 0)
        )
      ) {
        return;
      }
      await releaseRequest();
      if (stopped) {
        // A replay says ok: true, but this job produced nothing; the next click starts a new one.
        throw new Error("Prediction failed. Retry to start a new request.");
      }
    },
  };
}
